import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import {
  Shield,
  Users,
  Building2,
  Globe,
  Heart,
  Award,
  CheckCircle,
  MapPin,
  Plane,
  Home,
  ArrowRight
} from 'lucide-react';

export default function AboutPage() {
  const { t } = useLanguage();

  const highlights = [
    { label: t('about.highlight_germany', 'Germany-first relocation focus'), icon: MapPin },
    { label: t('about.highlight_tla', 'TLA eligibility signals on listings'), icon: Shield },
    { label: t('about.highlight_preview', 'Private preview as inventory grows'), icon: Building2 },
    { label: t('about.highlight_mission', 'Built for PCS and government moves'), icon: Users },
  ];

  const values = [
    {
      icon: Shield,
      title: t('about.value_trust', 'Trust & Transparency'),
      description: t('about.value_trust_desc', 'We surface pricing and TLA eligibility signals clearly. Standard listings have no commissions, and TLA booking success fees — when applicable — are disclosed before confirmation.'),
    },
    {
      icon: Heart,
      title: t('about.value_community', 'Military Community First'),
      description: t('about.value_community_desc', 'Built by military families, for military families. We understand the unique challenges of PCS moves.'),
    },
    {
      icon: Globe,
      title: t('about.value_support', '24/7 Support'),
      description: t('about.value_support_desc', 'Our dedicated team is available around the clock to assist with your relocation needs.'),
    },
    {
      icon: Award,
      title: t('about.value_quality', 'Quality Assured'),
      description: t('about.value_quality_desc', 'Listings go through owner onboarding and admin review before publication so quality stays consistent as inventory grows.'),
    },
  ];


  return (
    <div className="min-h-screen bg-slate-100 pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-signal-blue/10 to-transparent" />
        <div className="max-w-6xl mx-auto px-4 relative">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-800 mb-6">
              {t('about.hero_title', 'Making Military Moves Easier')}
            </h1>
            <p className="text-xl text-slate-600 mb-8">
              {t('about.hero_subtitle', 'TLA Housing Europe is a Germany-first relocation platform for U.S. military and government personnel seeking TLA-focused housing across Europe. We are in private preview while inventory and review capacity grow.')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/listings">
                <Button className="bg-military-gold hover:bg-military-gold/90 text-slate-950">
                  <Home className="w-4 h-4 mr-2" />
                  {t('about.explore_properties', 'Explore Properties')}
                </Button>
              </Link>
              <Link to="/register?role=owner">
                <Button variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100">
                  <Building2 className="w-4 h-4 mr-2" />
                  {t('about.list_property', 'List Your Property')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Launch focus */}
      <section className="py-16 border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {highlights.map((item, index) => (
              <div key={index} className="text-center">
                <item.icon className="w-8 h-8 text-military-gold mx-auto mb-3" />
                <div className="text-slate-700 text-sm font-medium leading-snug">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl font-bold text-slate-800 mb-6">
                {t('about.story_title', 'Our Story')}
              </h2>
              <div className="space-y-4 text-slate-600">
                <p>
                  {t('about.story_p1', 'TLA Housing Europe was born from firsthand experience. As military families ourselves, we understood the stress and uncertainty that comes with every PCS order.')}
                </p>
                <p>
                  {t('about.story_p2', 'Finding quality, TLA-compliant housing in a foreign country while managing a move across continents shouldn\'t be a nightmare. We created this platform to change that.')}
                </p>
                <p>
                  {t('about.story_p3', 'We are building a Germany-first marketplace with TLA eligibility signals, owner review, and relocation tools. Additional European corridors will follow our roadmap as listings and location coverage mature.')}
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-signal-blue/20 rounded-full flex items-center justify-center">
                    <Plane className="w-6 h-6 text-signal-blue" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-slate-800">
                      {t('about.mission_title', 'Our Mission')}
                    </h3>
                  </div>
                </div>
                <p className="text-slate-600 italic">
                  &ldquo;{t('about.mission_quote', 'To make every military relocation to Europe as smooth as possible by providing clear housing options, TLA-focused signals, and responsive support.')}&rdquo;
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 border-t border-slate-200 bg-white/50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-slate-800 mb-4">
              {t('about.values_title', 'Our Values')}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t('about.values_subtitle', 'These principles guide everything we do at TLA Housing Europe.')}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:border-signal-blue/50 transition-colors">
                <value.icon className="w-10 h-10 text-signal-blue mb-4" />
                <h3 className="font-heading text-lg font-bold text-slate-800 mb-2">
                  {value.title}
                </h3>
                <p className="text-slate-600 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-slate-800 mb-4">
              {t('about.team_title', 'Meet Our Team')}
            </h2>
          </div>
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
              <div className="w-16 h-16 bg-signal-blue/10 rounded-full mx-auto mb-5 flex items-center justify-center">
                <Users className="w-8 h-8 text-signal-blue" />
              </div>
              <p className="text-slate-600 leading-relaxed">
                {t('about.team_description', 'Our platform is built by a team focused on relocation housing quality, owner support, and military-friendly service across Europe.')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl font-bold text-slate-800 mb-4">
            {t('about.cta_title', 'Ready to Find Your European Home?')}
          </h2>
          <p className="text-slate-600 mb-8 max-w-2xl mx-auto">
            {t('about.cta_subtitle', 'Create a free account to browse listings, save favorites, and prepare for your PCS move during our private preview.')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button className="bg-signal-blue hover:bg-signal-blue-hover text-white">
                {t('about.get_started', 'Get Started Free')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100">
                {t('about.contact_us', 'Contact Us')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 border-t border-slate-200 bg-white/50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8">
            <div className="flex items-center gap-2 text-slate-600">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              <span className="text-sm">{t('about.badge_tla', 'TLA-focused')}</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              <span className="text-sm">{t('about.badge_support', 'Relocation support')}</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
